import { TourActions } from '../constants'
import { trackEvent } from 'aria-viewer/core/middleware/analyticsMiddleware'
import * as EVENTS from 'aria-viewer/analytics/constants/desktopAnalytics'
import {
    TRIGGER_IN_SCENE,
    HOTSPOT_IN_SCENE,
    NEXT_BUTTON_CLICKED,
    INFO_ICON_CLICKED,
    SOUND_BUTTON_MUTE,
    SOUND_BUTTON_UNMUTE,
    USER_REGISTRATION_OPT_IN_PROMPT_SHOWN,
    USER_REGISTRATION_OPT_IN_PROMPT_BUTTON_CLICK,
} from 'aria-viewer/analytics/constants'
import { EXPERIENCELIST_EXPERIENCE_SELECTED } from 'aria-viewer/analytics/constants'
import { getTourData } from 'aria-viewer/analytics/utils/analyticUtils'
import { hasNonEnglishLangugages } from 'aria-viewer/utils/functions'
import { STOP_ANALYTICS_INITIATED_BY } from 'aria-viewer/tour/constants/'
import {
    SET_CONTEXT_MENU,
    TOUR_LANGUAGE_MENU,
    OVERFLOW_MENU,
    SHARE_MENU,
} from 'aria-viewer/contextmenu/contextMenuActions'
import { LANGUAGE_CHANGE } from 'aria-viewer/language/languageActions'
import {
    ANALYTICS_EXP_SELECTED,
    ANALYTICS_CONTEXTMENU_ITEM_CLICKED,
    ANALYTICS_SET_STOP_INITIATED_BY,
    ANALYTICS_INFO_BUTTON_CLICKED,
    ANALYTICS_AUDIO_BUTTON_CLICKED,
} from 'aria-viewer/analytics/analyticActions'
import { AUDIO_TOUR, AUDIO_PLAY } from 'aria-viewer/audiomanager/audioManagerActions'
import { SHARE_TYPES } from 'aria-viewer/tour/constants'
import { mainMedia } from '../../utils/functions'
import idx from 'idx'

const checkTourLoaded = (getState, action) => {
    const hasTourLoaded = action.value.tour === false
    if (hasTourLoaded) {
        const { loc_name, loc_id, inst_name, owner_id, userkey, type, totalStops, isClient } = getTourData()(getState)
        const state = getState()

        return trackEvent(EVENTS.EXP_LOADED, {
            Institution: inst_name,
            Location: loc_name,
            Inst_ID: owner_id,
            Loc_ID: loc_id,
            Userkey: userkey,
            Exp_Type: type,
            Total_Stops: totalStops,
            Is_Client: isClient,
            Other_Lang_Included: hasNonEnglishLangugages(state),
        })
    }
}

const tourAnalytics = {
    // Desktop experience loaded.
    [TourActions.LOADING]: (getState, action) => {
        if (action.value.tour != undefined) return checkTourLoaded(getState, action)
    },

    // Current stop has changed
    [TourActions.STOP_CHANGED]: (getState, action) => {
        const state = getState()
        const {
            loc_id,
            stoptitle,
            stopIndex,
            type,
            totalStops,
            stopVisitedCount,
            inst_name,
            owner_id,
            loc_name,
            stopid,
        } = getTourData()(getState)

        const initiatedBy = state.getIn(['analytics', 'stopInitiatedBy']) || STOP_ANALYTICS_INITIATED_BY.first_stop
        const hotspots = state.getIn(['interactiveElements', 'hotspots'])
        const triggers = state.getIn(['interactiveElements', 'triggers'])
        const id = idx(mainMedia(state), _ => _.id) || state.getIn(['tour', 'stops', 'list']).get(0).main_media_id
        const hotspot = hotspots[id]
        const trigger = triggers[id]

        let events = []

        if (stopVisitedCount < EVENTS.EXP_STOP_LOADED_MAX_EVENTS_SENT) {
            events.push(
                trackEvent(EVENTS.EXP_STOP_LOADED, {
                    Loc_ID: loc_id,
                    Stop: stoptitle,
                    Stop_Order: stopIndex,
                    Initiated_by: initiatedBy,
                    Exp_Type: type,
                    Total_Stops: totalStops,
                })
            )
        }

        if (hotspot && hotspot.length > 0) {
            events.push(
                trackEvent(HOTSPOT_IN_SCENE, {
                    Institution: inst_name,
                    Inst_ID: owner_id,
                    Location: loc_name,
                    Loc_ID: loc_id,
                    Stop: stoptitle,
                    Stop_ID: stopid,
                })
            )
        }

        if (trigger && trigger.length > 0) {
            events.push(
                trackEvent(TRIGGER_IN_SCENE, {
                    Institution: inst_name,
                    Inst_ID: owner_id,
                    Location: loc_name,
                    Loc_ID: loc_id,
                    Stop: stoptitle,
                    Stop_ID: stopid,
                })
            )
        }

        if (events.length > 0) return events
    },

    [ANALYTICS_SET_STOP_INITIATED_BY]: (getState, action) => {
        if (action.initiatedBy == STOP_ANALYTICS_INITIATED_BY.next_button) {
            const { inst_name, owner_id, loc_name, loc_id, stoptitle, stopid } = getTourData()(getState)

            return trackEvent(NEXT_BUTTON_CLICKED, {
                Institution: inst_name,
                Inst_ID: owner_id,
                Location: loc_name,
                Loc_ID: loc_id,
                Stop: stoptitle,
                Stop_ID: stopid,
            })
        }
    },

    [ANALYTICS_INFO_BUTTON_CLICKED]: (getState, action) => {
        const { inst_name, owner_id, loc_name, loc_id, stoptitle, stopid } = getTourData()(getState)

        return trackEvent(INFO_ICON_CLICKED, {
            Institution: inst_name,
            Inst_ID: owner_id,
            Location: loc_name,
            Loc_ID: loc_id,
            Stop: stoptitle,
            Stop_ID: stopid,
        })
    },

    [ANALYTICS_AUDIO_BUTTON_CLICKED]: (getState, action) => {
        const { inst_name, owner_id, loc_name, loc_id, stoptitle, stopid } = getTourData()(getState)
        const audioState = getState().getIn(['audioManager', 'state', AUDIO_TOUR])

        return trackEvent(audioState == AUDIO_PLAY ? SOUND_BUTTON_MUTE : SOUND_BUTTON_UNMUTE, {
            Institution: inst_name,
            Inst_ID: owner_id,
            Location: loc_name,
            Loc_ID: loc_id,
            Stop: stoptitle,
            Stop_ID: stopid,
        })
    },

    [TourActions.SET_REGISTRATION_ENABLED]: (getState, action) => {
        const { inst_name, loc_name, owner_id, loc_id } = getTourData()(getState)

        return trackEvent(EVENTS.EXP_REGAVAILABLE, {
            Institution: inst_name,
            Location: loc_name,
            Inst_ID: owner_id,
            Loc_ID: loc_id,
            isAvailable: action.enabled,
        })
    },

    //Launch ADA mode
    [TourActions.LAUNCH_ADA_MODE]: (getState, action) => {
        const { inst_id, loc_id } = action.value

        return trackEvent(EVENTS.EXP_ADA_MODE, {
            Loc_ID: loc_id,
            Inst_ID: inst_id,
        })
    },

    [SET_CONTEXT_MENU]: (getState, action) => {
        let eventToTrack

        const { inst_name, owner_id, stoptitle } = getTourData()(getState)

        const currentContextMenu = action.contextMenu.name

        switch (currentContextMenu) {
            case TOUR_LANGUAGE_MENU:
                eventToTrack = EVENTS.EXP_LANGUAGE_ICON_CLICKED
                break
            case OVERFLOW_MENU:
                eventToTrack = EVENTS.MOREICON_CLICKED
                break
            case SHARE_MENU:
                eventToTrack = EVENTS.SHARE_CLICKED
                break
            default:
                eventToTrack = null
        }

        return (
            eventToTrack &&
            trackEvent(eventToTrack, {
                Institution: inst_name,
                Inst_ID: owner_id,
                Scene_Name: stoptitle,
            })
        )
    },

    [LANGUAGE_CHANGE]: (getState, action) => {
        const { inst_name, owner_id, stoptitle } = getTourData()(getState)
        const { language } = action.language

        return (
            stoptitle &&
            trackEvent(EVENTS.EXP_LANGUAGE_SELECTED, {
                Institution: inst_name,
                Inst_ID: owner_id,
                Scene_Name: stoptitle,
                Selected_Language: language,
            })
        )
    },

    [ANALYTICS_EXP_SELECTED]: (getState, action) => {
        const { inst_name, owner_id, loc_name, loc_id, stoptitle, stopid } = getTourData()(getState)

        return trackEvent(EXPERIENCELIST_EXPERIENCE_SELECTED, {
            Institution: inst_name,
            Inst_ID: owner_id,
            Location: loc_name,
            Loc_ID: loc_id,
            Stop: stoptitle,
            Stop_ID: stopid,
        })
    },

    [ANALYTICS_CONTEXTMENU_ITEM_CLICKED]: (getState, action) => {
        const { inst_name, owner_id, stoptitle } = getTourData()(getState)

        let eventToTrack

        switch (action.itemName) {
            case SHARE_TYPES.email:
                eventToTrack = EVENTS.SHARE_EMAIL_CLICKED
                break
            case SHARE_TYPES.facebook:
                eventToTrack = EVENTS.SHARE_FACEBOOK_CLICKED
                break
            case SHARE_TYPES.link:
                eventToTrack = EVENTS.SHARE_LINK_CLICKED
                break
            case SHARE_TYPES.linkedin:
                eventToTrack = EVENTS.SHARE_LINKEDIN_CLICKED
                break
            case SHARE_TYPES.twitter:
                eventToTrack = EVENTS.SHARE_TWITTER_CLICKED
                break
            default:
                return
        }

        return trackEvent(eventToTrack, {
            Institution: inst_name,
            Inst_ID: owner_id,
            Scene_Name: stoptitle,
        })
    },

    [TourActions.IS_WINDOW_FULLSCREEN]: (getState, action) => {
        const { inst_name, owner_id, stoptitle } = getTourData()(getState)

        return trackEvent(EVENTS.MOREICON_FULLSCREEN_CLICKED, {
            Institution: inst_name,
            Inst_ID: owner_id,
            Scene_Name: stoptitle,
        })
    },

    [USER_REGISTRATION_OPT_IN_PROMPT_SHOWN]: (getState, action) => {
        return trackEvent(USER_REGISTRATION_OPT_IN_PROMPT_SHOWN)
    },

    [USER_REGISTRATION_OPT_IN_PROMPT_BUTTON_CLICK]: (getState, action) => {
        return trackEvent(USER_REGISTRATION_OPT_IN_PROMPT_BUTTON_CLICK, {
            Button_Clicked: action.selectedOption,
        })
    },
}

export default tourAnalytics
